import { hasValue } from '@core/utils/equality';

export type UrlParams = Record<string, object | string | number | boolean | undefined | null>;

export const MAX_URL_LENGTH = 2083;

export type UrlFromParamsOpts = {
  queryParams: UrlParams;
  pathParams: UrlParams;
  urlSplits: string[];
  onUrlTooLong?: (info: { url: string; length: number }) => void;
};

/**
 * Given a record of objects/arrays/strings, flatten it into a URL-processable
 * string[][] object. Ported from slab-web's useSlabQuery.tsx so both apps build
 * URLs the same way against the shared route tables in this directory.
 */
export const flattenRecord = (params: UrlParams): [string, string][] => {
  const result: [string, string][] = [];
  Object.entries(params).forEach(([key, val]) => {
    if (Array.isArray(val)) {
      if (val.length === 0) return;
      if (typeof val[0] !== 'object') {
        val.forEach((subVal) => {
          result.push([key, String(subVal)]);
        });
      } else {
        val.forEach((subObj, idx) => {
          if (!hasValue(subObj)) {
            return;
          }
          const flattened = flattenRecord(subObj as UrlParams);
          flattened.forEach(([subKey, subVal]) => {
            result.push([`${key}.${idx}.${subKey}`, subVal]);
          });
        });
      }
    } else if (typeof val === 'object') {
      if (!hasValue(val)) {
        return;
      }
      const flattened = flattenRecord(val as UrlParams);
      flattened.forEach(([subKey, subVal]) => {
        result.push([`${key}.${subKey}`, subVal]);
      });
    } else if (val === true || val === false) {
      result.push([key, String(val)]);
    } else if (hasValue(val)) {
      result.push([key, String(val)]);
    }
  });

  return result;
};

/**
 * Builds a full URL (path + query string) from a route's `urlSplits` plus the
 * caller's path/query params. `urlSplits` alternates static path segments with
 * bare param-name placeholders (e.g. `['/slab/companies/', 'id']`), matching
 * the shape used throughout routes.ts/routeVals.ts.
 */
export const urlFromParams = ({
  queryParams,
  pathParams,
  urlSplits,
  onUrlTooLong,
}: UrlFromParamsOpts): string => {
  const flattenedQueryParams = flattenRecord(queryParams);
  const queryString = String(new URLSearchParams(flattenedQueryParams));
  const queryPart = queryString === '' ? '' : `?${queryString}`;

  const stringifiedPathParams = Object.fromEntries(
    Object.entries(pathParams)
      .filter(([, val]) => hasValue(val))
      .map(([key, val]) => [key, String(val)]),
  );
  const basePath = urlSplits.map((segment) => stringifiedPathParams[segment] ?? segment).join('');

  const url = `${basePath}${queryPart}`;
  if (onUrlTooLong && url.length >= MAX_URL_LENGTH) {
    onUrlTooLong({ url, length: url.length });
  }

  return url;
};
